/**
 * The resolved RestoID theme, as a React value.
 * ---------------------------------------------------------------------------
 * The source of truth is `html[data-rid-theme]`, written by the blocking script in
 * index.html's <head> before first paint and updated by the header switch in
 * custom/chrome/restoid-chrome.ts. Almost nothing needs this hook — colors reach
 * components through the `--rid-*` tokens, which CSS resolves on its own.
 *
 * It exists for the one case CSS cannot cover: a third-party widget that has to be
 * TOLD which variant to render (the Yandex ID Suggest button), where our own
 * placeholder must match whatever the vendor draws.
 */
import { useEffect, useState } from 'react';

type RidTheme = 'light' | 'dark';

export const readRidTheme = (): RidTheme =>
  document.documentElement.dataset.ridTheme === 'light' ? 'light' : 'dark';

const useRidTheme = (): RidTheme => {
  const [theme, setTheme] = useState<RidTheme>(readRidTheme);

  useEffect(() => {
    const sync = (): void => {
      setTheme(readRidTheme());
    };
    // Re-read on mount as well: the attribute can have changed between the initial
    // state being computed and the effect running.
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-rid-theme'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return theme;
};

export default useRidTheme;
